import React, { useState } from 'react';
import { Leaf, Sparkles } from 'lucide-react';
import Logo from '../../components/Logo';
import QuizBuilder, { QuizQuestion } from '../../components/landing/QuizBuilder';
import EmailCaptureForm from '../../components/landing/EmailCaptureForm';
import { SEED_PACKAGES, PRODUCT_INFO, ProductDetails, SeedPackageType } from './constants';

export default function QuizLanding() {
  const [quizComplete, setQuizComplete] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string>>({});
  const [recommendedProduct, setRecommendedProduct] = useState<ProductDetails | null>(null);
  const [recommendedSeedPackage, setRecommendedSeedPackage] = useState<SeedPackageType>('herb_garden');
  const [showEmailCapture, setShowEmailCapture] = useState(false);

  const questions: QuizQuestion[] = [
    {
      id: 'zipcode',
      question: "What's your zip code?",
      type: 'zipcode',
      placeholder: '12345',
      validation: (value) => /^\d{5}$/.test(value)
    },
    {
      id: 'space',
      question: 'Where will you be growing?',
      type: 'radio',
      options: [
        { value: 'apartment', label: 'Apartment', description: 'Limited indoor space' },
        { value: 'house', label: 'House with yard', description: 'Outdoor growing available' },
        { value: 'countertop', label: 'Kitchen countertop', description: 'Small indoor space' },
        { value: 'balcony', label: 'Balcony/Patio', description: 'Outdoor container space' }
      ]
    },
    {
      id: 'light',
      question: 'How much natural light is available?',
      type: 'radio',
      options: [
        { value: 'full_sun', label: 'Full sun (6+ hours)', description: 'Direct sunlight most of the day' },
        { value: 'partial_sun', label: 'Partial sun (4-6 hours)', description: 'Some direct sunlight' },
        { value: 'bright_indirect', label: 'Bright indirect light', description: 'Near a sunny window' },
        { value: 'low_light', label: 'Low light', description: 'No direct sunlight' }
      ]
    },
    {
      id: 'time',
      question: 'How much time can you spend per week?',
      type: 'radio',
      options: [
        { value: '5_min', label: '5 minutes or less', description: 'Just quick check-ins' },
        { value: '15_min', label: '15-30 minutes', description: 'Some hands-on time' },
        { value: '1_hour', label: '1 hour or more', description: 'Enjoy spending time gardening' }
      ]
    },
    {
      id: 'goal',
      question: 'What do you want to grow?',
      type: 'radio',
      options: [
        { value: 'herbs', label: 'Fresh herbs', description: 'Basil, cilantro, parsley for cooking' },
        { value: 'salad', label: 'Salad greens', description: 'Lettuce, arugula for fresh salads' },
        { value: 'greens', label: 'Cooking greens', description: 'Kale, chard, spinach' },
        { value: 'variety', label: 'A bit of everything', description: 'Mix of herbs and greens' }
      ]
    },
    {
      id: 'confidence',
      question: "What's your gardening experience?",
      type: 'radio',
      options: [
        { value: 'never', label: 'Never grown anything', description: 'Complete beginner' },
        { value: 'once', label: 'Tried once or twice', description: 'Limited experience' },
        { value: 'some', label: 'Some experience', description: 'Grown a few plants successfully' },
        { value: 'experienced', label: 'Pretty experienced', description: 'Regular gardener' }
      ]
    }
  ];

  const handleQuizComplete = (answers: Record<string, string>) => {
    setQuizAnswers(answers);
    
    // Determine recommended product and seed package based on answers
    let seedPackage: SeedPackageType = 'herb_garden';
    
    if (answers.goal === 'herbs') {
      seedPackage = 'herb_garden';
    } else if (answers.goal === 'salad') {
      seedPackage = 'salad_mix';
    } else if (answers.goal === 'greens') {
      seedPackage = 'greens_bag';
    } else if (answers.goal === 'variety') {
      seedPackage = 'chefs_choice';
    }
    
    setRecommendedSeedPackage(seedPackage);
    
    // For simplicity, always recommend The Grow Box
    const product: ProductDetails = {
      id: PRODUCT_INFO['The Grow Box'].slug,
      name: 'The Grow Box',
      price: PRODUCT_INFO['The Grow Box'].price,
      slug: PRODUCT_INFO['The Grow Box'].slug,
      external_url: PRODUCT_INFO['The Grow Box'].external_url,
      description: 'Everything you need to start growing'
    };
    
    setRecommendedProduct(product);
    setQuizComplete(true);
  };

  const getRecommendationReason = () => {
    if (!quizAnswers.goal || !quizAnswers.confidence || !quizAnswers.space) {
      return "Based on your answers, we've selected the perfect grow box for you.";
    }
    
    const reasons = [];
    
    if (quizAnswers.confidence === 'never' || quizAnswers.confidence === 'once') {
      reasons.push("Perfect for beginners with easy-to-follow instructions");
    }
    
    if (quizAnswers.time === '5_min') {
      reasons.push("Low maintenance with self-watering system");
    }
    
    if (quizAnswers.space === 'apartment' || quizAnswers.space === 'countertop') {
      reasons.push("Compact size perfect for small spaces");
    }
    
    if (reasons.length === 0) {
      reasons.push("Complete growing system with everything included");
    }
    
    return reasons.join(". ") + ".";
  };

  const getGrowingEnvironment = () => {
    if (quizAnswers.space === 'house' || quizAnswers.space === 'balcony') {
      return 'outdoor';
    }
    return 'indoor';
  };

  if (!quizComplete) {
    return (
      <div className="min-h-screen bg-[#F4F2EE]">
        {/* Hero */}
        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="flex justify-center mb-8">
              <Logo size="lg" showText={false} variant="full" />
            </div>

            <div className="max-w-3xl mx-auto text-center mb-8">
              <h1 className="text-3xl md:text-5xl font-bold mb-4 text-gray-900">
                Find Your Perfect Grow Box in 30 Seconds
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-6">
                Answer a few quick questions and we'll match you with the ideal growing setup
              </p>

              <div className="flex flex-wrap justify-center gap-3 mb-8">
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  ✓ No experience needed
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  ✓ Takes under a minute
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Quiz */}
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <QuizBuilder
              questions={questions}
              onComplete={handleQuizComplete}
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Results Hero */}
      <section className="bg-gradient-to-br from-primary-500 to-primary-600 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 p-4 rounded-full">
                <Sparkles className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 text-white">
              Your Perfect Match!
            </h1>
            <p className="text-xl text-white/90">
              Based on your answers, here's what we recommend
            </p>
          </div>
        </div>
      </section>

      {/* Personalized Result */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Seed Package Match */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-8 mb-8">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">
                  {SEED_PACKAGES[recommendedSeedPackage].emoji}
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {SEED_PACKAGES[recommendedSeedPackage].name}
                </h2>
                <p className="text-lg text-gray-700">
                  {SEED_PACKAGES[recommendedSeedPackage].description}
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 mb-6">
                <h3 className="font-bold text-lg mb-3 text-gray-900">What you'll grow:</h3>
                <ul className="grid grid-cols-2 gap-3">
                  {SEED_PACKAGES[recommendedSeedPackage].includes.map((plant, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-700">
                      <Leaf className="h-4 w-4 text-green-600" />
                      <span>{plant}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-center">
                <h3 className="font-bold text-lg mb-2 text-gray-900">Why this is perfect for you:</h3>
                <p className="text-gray-700">
                  {getRecommendationReason()}
                </p>
              </div>
            </div>

            {/* Product Display */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                The Grow Box - ${PRODUCT_INFO['The Grow Box'].price}
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>10-gallon grow bag</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Your {SEED_PACKAGES[recommendedSeedPackage].name} seed packet</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Organic fertilizer & self-watering spike</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Zone-matched planting plan & video tutorials</span>
                </li>
              </ul>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 text-center">
                  <strong>Setup time:</strong> Under 10 minutes • <strong>Maintenance:</strong> 5 minutes per week
                </p>
              </div>
            </div>

            {/* Email Capture */}
            {!showEmailCapture ? (
              <div className="text-center">
                <button
                  onClick={() => setShowEmailCapture(true)}
                  className="bg-primary-500 text-white py-4 px-8 rounded-lg hover:bg-primary-600 transition-all duration-300 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 inline-flex items-center gap-2"
                >
                  <Leaf className="h-6 w-6" />
                  Start My Garden
                </button>
              </div>
            ) : (
              <div className="bg-white border-2 border-primary-200 rounded-lg p-6">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Almost there! Let's complete your order
                  </h3>
                </div>
                <EmailCaptureForm
                  product={recommendedProduct!}
                  sourcePage="quiz"
                  seedPackage={recommendedSeedPackage}
                  growingEnvironment={getGrowingEnvironment() as 'indoor' | 'outdoor'}
                  quizAnswers={quizAnswers}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
